import { ReactNode } from 'react'

interface ApprovalCardProps {
    /** Quando definido, o card mostra o checkbox de seleção. */
    selectable?: boolean
    selected?: boolean
    onToggleSelect?: () => void
    /** Linha superior: data/etiqueta da solicitação. */
    eyebrow?: ReactNode
    title: ReactNode
    subtitle?: ReactNode
    /** Valor em destaque, no canto superior direito. */
    amount: ReactNode
    amountHint?: ReactNode
    /** Corpo livre: laudo do comprovante, motivo, marcações. */
    children?: ReactNode
    /** Botões da linha de baixo. */
    actions?: ReactNode
}

/**
 * Card de uma solicitação. Substitui as três tabelas da tela antiga.
 *
 * Tabela obrigava rolagem horizontal no celular — e esta é uma tela que o
 * financeiro abre no telefone para liberar pagamento. No card, o que decide a
 * aprovação (valor, contraparte, comprovante) fica visível sem rolar, e os botões
 * ficam sempre no mesmo lugar.
 */
export default function ApprovalCard({
    selectable = false,
    selected = false,
    onToggleSelect,
    eyebrow,
    title,
    subtitle,
    amount,
    amountHint,
    children,
    actions,
}: ApprovalCardProps) {
    return (
        <div
            className={`rounded-xl border bg-base-100 transition-colors ${
                selected
                    ? 'border-secondary ring-1 ring-secondary/30 bg-secondary/[0.03]'
                    : 'border-base-300/70 hover:border-base-300'
            }`}
        >
            <div className="p-4 flex gap-3">
                {selectable && (
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm checkbox-secondary mt-1 shrink-0"
                        checked={selected}
                        onChange={onToggleSelect}
                        aria-label="Selecionar solicitação"
                    />
                )}

                <div className="flex-1 min-w-0 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            {eyebrow && (
                                <div className="text-xs text-base-content/50 mb-1 flex items-center gap-2 flex-wrap">
                                    {eyebrow}
                                </div>
                            )}
                            <div className="font-medium text-base-content break-words">
                                {title}
                            </div>
                            {subtitle && (
                                <div className="text-sm text-base-content/60 mt-0.5 break-words">
                                    {subtitle}
                                </div>
                            )}
                        </div>

                        <div className="text-right shrink-0">
                            <div className="font-mono font-semibold whitespace-nowrap">
                                {amount}
                            </div>
                            {amountHint && (
                                <div className="text-xs text-base-content/50 mt-0.5 whitespace-nowrap">
                                    {amountHint}
                                </div>
                            )}
                        </div>
                    </div>

                    {children}

                    {actions && (
                        <div className="flex items-center justify-end gap-2 flex-wrap pt-1">
                            {actions}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
